import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../../../i18n'
import { useAuth } from '../../AuthProvider'
import { Button } from '../../ui/Button'
import {
  fetchControlExecutionDetail,
  type ControlExecutionDetail,
  type ControlOverviewRow,
} from '../../../services/controlCentricWorkflowApi'
import { featureFlags } from '../../../config/featureFlags'
import {
  journeyResumeStorageKey,
  readJourneyResumePointer,
  resolveControlCentricResumeIndex,
  writeJourneyResumePointer,
} from '../../../pages/compliance-center/journeyResumePosition'
import { ControlQuestionAnswerCard, type QuestionRow } from './ControlQuestionAnswerCard'
import { ControlContextPanel } from './ControlContextPanel'

type Lang = 'en' | 'ru' | 'es'

type Loc = { en?: string; ru?: string; es?: string }

export type ControlCentricStep = QuestionRow & {
  controlKey: string
  controlTitle: string
  controlDescription?: string
  soc2Criteria?: string | null
  criteriaReference?: string | null
  evidenceExpectations?: Array<{ requirementKey: string; title: string }>
  recommendation?: { localization?: Loc }
  expectedEvidenceHint?: string | null
  legacyControlCode: string | null
}

type Props = {
  sessionId: string
  standardCode: string
  lang: Lang
  controls: ControlOverviewRow[]
  govEvidence?: Array<{ id: string; title?: string; freshnessStatus?: string }>
  onSaveQuestionAnswer: (
    params: {
      domainKey: string
      questionKey: string
      canonicalControlCode: string | null
      answerValue: string
      assessorNote: string
    },
    options?: { finishAfterSave?: boolean },
  ) => Promise<void>
  onStepChange?: (step: ControlCentricStep | null, index: number) => void
  initialControlKey?: string | null
  remediationMode?: boolean
  remediationFilter?: import('../../../pages/compliance-center/journeyRemediationNav').RemediationFilterTab
}

function buildStepShell(row: ControlOverviewRow, standardCode: string): ControlCentricStep {
  return {
    controlKey: row.controlKey,
    controlTitle: row.title || row.controlKey,
    controlDescription: row.description,
    soc2Criteria: null,
    criteriaReference: null,
    evidenceExpectations: [],
    recommendation: undefined,
    questionKey: row.primaryQuestionKey || row.controlKey,
    domainKey: standardCode,
    legacyControlCode: row.controlKey,
    localization: { assertion: { en: row.title || row.controlKey } },
    trustCriteria: [],
    answerValue: null,
    assessorNote: null,
  }
}

function mergeExecutionDetail(shell: ControlCentricStep, detail: ControlExecutionDetail, standardCode: string): ControlCentricStep {
  const primary = (detail.questions || [])[0]
  if (!primary) {
    return {
      ...shell,
      controlTitle: detail.title || shell.controlTitle,
      controlDescription: detail.description || shell.controlDescription,
      soc2Criteria: detail.soc2Criteria,
      criteriaReference: detail.criteriaReference ?? detail.soc2Criteria,
      evidenceExpectations: detail.evidenceExpectations,
      recommendation: detail.recommendation,
      expectedEvidenceHint: detail.expectedEvidenceHint,
    }
  }
  return {
    controlKey: shell.controlKey,
    controlTitle: detail.title || shell.controlTitle,
    controlDescription: detail.description || shell.controlDescription,
    soc2Criteria: detail.soc2Criteria,
    criteriaReference: detail.criteriaReference ?? detail.soc2Criteria,
    evidenceExpectations: detail.evidenceExpectations,
    recommendation: detail.recommendation,
    expectedEvidenceHint: detail.expectedEvidenceHint,
    questionKey: primary.questionKey,
    domainKey: primary.domainKey || standardCode,
    legacyControlCode: primary.legacyControlCode || detail.legacyControlCode || shell.controlKey,
    localization: primary.localization,
    trustCriteria: primary.trustCriteria,
    answerValue: primary.answerValue,
    assessorNote: primary.assessorNote,
  }
}

export function ControlCentricSequentialWorkbench({
  sessionId,
  standardCode,
  lang,
  controls,
  govEvidence,
  onSaveQuestionAnswer,
  onStepChange,
  initialControlKey = null,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const [steps, setSteps] = useState<ControlCentricStep[]>([])
  const [stepIndex, setStepIndex] = useState(0)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [answer, setAnswer] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const onStepChangeRef = useRef(onStepChange)
  onStepChangeRef.current = onStepChange

  const controlsSignature = useMemo(
    () => controls.map((c) => `${c.controlKey}:${c.primaryQuestionKey || ''}:${c.title || ''}`).join('|'),
    [controls],
  )

  useEffect(() => {
    if (!controls.length) {
      setSteps([])
      setStepIndex(0)
      return
    }
    const built = controls.map((row) => buildStepShell(row, standardCode))
    setSteps(built)
    const resumeKey = journeyResumeStorageKey(sessionId, standardCode, 'control-centric')
    const saved = readJourneyResumePointer(resumeKey)
    let idx = resolveControlCentricResumeIndex(built, saved)
    if (initialControlKey) {
      const byControl = built.findIndex((s) => s.controlKey === initialControlKey)
      if (byControl >= 0) idx = byControl
    }
    setStepIndex(idx)
  }, [sessionId, standardCode, controlsSignature, controls.length, initialControlKey])

  const current = steps[stepIndex] || null
  const resumeKey = journeyResumeStorageKey(sessionId, standardCode, 'control-centric')

  useEffect(() => {
    if (!token || !sessionId || !current?.controlKey) return
    let cancelled = false
    setLoadingDetail(true)
    setDetailError(null)
    void fetchControlExecutionDetail(token, sessionId, current.controlKey, lang)
      .then((detail) => {
        if (cancelled) return
        setSteps((prev) =>
          prev.map((step, idx) => (idx === stepIndex ? mergeExecutionDetail(step, detail, standardCode) : step)),
        )
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : t('common.error', 'Failed to load control'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, sessionId, stepIndex, current?.controlKey, lang, standardCode, t])

  useEffect(() => {
    onStepChangeRef.current?.(current, stepIndex)
  }, [current, stepIndex])

  useEffect(() => {
    if (!current || loadingDetail) return
    writeJourneyResumePointer(resumeKey, {
      domainKey: current.domainKey || standardCode,
      questionKey: current.questionKey,
      controlKey: current.controlKey,
      stepIndex,
    })
  }, [current?.questionKey, current?.domainKey, current?.controlKey, stepIndex, resumeKey, loadingDetail, standardCode])

  useEffect(() => {
    if (!current) return
    const av = String(current.answerValue || '').toUpperCase()
    if (['YES', 'PARTIAL', 'NO', 'NOT_APPLICABLE', 'N/A'].includes(av)) {
      setAnswer(av === 'N/A' ? 'NOT_APPLICABLE' : av)
    } else setAnswer('')
    setNote(current.assessorNote || '')
    setSaveError(null)
  }, [current?.questionKey, current?.answerValue, current?.assessorNote])

  const goStep = useCallback(
    (next: number) => {
      setStepIndex(Math.max(0, Math.min(steps.length - 1, next)))
    },
    [steps.length],
  )

  const handleSaveAndNext = async () => {
    if (!current || !answer) {
      setSaveError(t('complianceCenterUi.journey.errors.noAnswer', 'Select an answer before saving.'))
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const isLast = stepIndex >= steps.length - 1
      await onSaveQuestionAnswer(
        {
          domainKey: current.domainKey || standardCode,
          questionKey: current.questionKey,
          canonicalControlCode: current.legacyControlCode,
          answerValue: answer,
          assessorNote: note,
        },
        { finishAfterSave: isLast },
      )
      const nextIdx = stepIndex < steps.length - 1 ? stepIndex + 1 : stepIndex
      goStep(nextIdx)
      const next = steps[nextIdx]
      if (next) {
        writeJourneyResumePointer(resumeKey, {
          domainKey: next.domainKey || standardCode,
          questionKey: next.questionKey,
          controlKey: next.controlKey,
          stepIndex: nextIdx,
        })
      }
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : t('complianceCenterUi.journey.errors.save', 'Failed to save answer.'))
    } finally {
      setSaving(false)
    }
  }

  const useControlStepLabels =
    featureFlags.STANDARD_CONTROL_CENTRIC_UX_V1 ||
    featureFlags.ENTERPRISE_AUTHORITATIVE_CORPUS_FREEZE_V1 ||
    featureFlags.ENTERPRISE_OPERATIONAL_SEMANTIC_CORPUS_V1 ||
    featureFlags.SOC2_AUTHORITATIVE_CORPUS_V1

  const unansweredAhead = useMemo(() => {
    if (!steps.length) return 0
    if (useControlStepLabels) {
      return Math.max(0, steps.length - stepIndex - 1)
    }
    return steps.slice(stepIndex).filter((s) => !s.answerValue).length
  }, [steps, stepIndex, useControlStepLabels])

  if (!controls.length) {
    return (
      <p className="text-sm text-slate-600">
        {t('controlCentric.sequential.noSteps', 'No assessment questions available for this session.')}
      </p>
    )
  }

  if (!current) return null

  const progressEyebrow = useControlStepLabels
    ? t('controlCentric.sequential.controlLabel', 'Control {{n}} / {{total}}', {
        n: stepIndex + 1,
        total: steps.length,
      })
    : `${t('controlCentric.sequential.stepLabel', 'Guided step')} ${stepIndex + 1} / ${steps.length}`

  const progressBadge =
    unansweredAhead > 0
      ? useControlStepLabels
        ? t('controlCentric.sequential.controlsRemaining', '{{n}} controls remaining', { n: unansweredAhead })
        : t('controlCentric.sequential.remaining', '{{n}} open ahead', { n: unansweredAhead })
      : null

  return (
    <div className="space-y-4">
      <ControlContextPanel
        controlKey={current.controlKey}
        title={current.controlTitle}
        description={current.controlDescription}
        soc2Criteria={current.soc2Criteria}
        criteriaReference={current.criteriaReference}
        standardCode={standardCode}
        evidenceExpectations={current.evidenceExpectations}
        progressEyebrow={progressEyebrow}
        progressBadge={progressBadge}
      />

      {loadingDetail ? (
        <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
      ) : detailError ? (
        <p className="text-sm text-red-600">{detailError}</p>
      ) : (
        <ControlQuestionAnswerCard
          lang={lang}
          sessionId={sessionId}
          standardCode={standardCode}
          question={current}
          recommendation={current.recommendation}
          expectedEvidenceHint={current.expectedEvidenceHint}
          answer={answer}
          note={note}
          saving={saving}
          saveError={saveError}
          govEvidence={govEvidence}
          onAnswerChange={setAnswer}
          onNoteChange={setNote}
          onSave={() => void handleSaveAndNext()}
          hidePrimarySave
          footer={
            <>
              <Button
                type="button"
                size="small"
                color="outline"
                disabled={stepIndex <= 0 || saving}
                onClick={() => goStep(stepIndex - 1)}
              >
                {t('complianceCenterUi.journey.sequentialPrev', 'Previous')}
              </Button>
              <Button
                type="button"
                size="small"
                color="primary"
                className="!bg-green-600 hover:!bg-green-700 !border-green-600"
                disabled={saving || !answer}
                onClick={() => void handleSaveAndNext()}
              >
                {saving
                  ? t('common.saving', 'Saving…')
                  : stepIndex >= steps.length - 1
                    ? t('controlCentric.sequential.saveFinish', 'Save & finish')
                    : t('controlCentric.sequential.saveNext', 'Save & next')}
              </Button>
            </>
          }
        />
      )}
    </div>
  )
}
